"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { Icon } from "@/components/ui/Icon";
import { Analytics } from "@/components/analytics/Analytics";
import { publicEnv } from "@/lib/env";
import {
  type ConsentState,
  CONSENT_EVENT,
  readConsent,
  writeConsent,
  setDefaultConsent,
  updateConsent,
} from "@/lib/consent";

const trackingConfigured = !!(publicEnv.gtmId || publicEnv.gaId);

export function CookieConsent() {
  const [consent, setConsent] = useState<ConsentState | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefStats, setPrefStats] = useState(false);
  const [prefMarketing, setPrefMarketing] = useState(false);

  // Initialisierung: Consent-Mode-Default (denied) + gespeicherte Wahl laden.
  useEffect(() => {
    setDefaultConsent();
    const stored = readConsent();
    if (stored) {
      // localStorage gibt es erst im Browser – Laden nach dem Mount ist hier beabsichtigt.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setConsent(stored);
      updateConsent(stored);
      setPrefStats(stored.statistics);
      setPrefMarketing(stored.marketing);
    } else {
      setShowBanner(true);
    }
  }, []);

  // Erlaubt das erneute Öffnen der Einstellungen (z. B. aus dem Footer).
  useEffect(() => {
    const open = () => {
      const stored = readConsent();
      setPrefStats(stored?.statistics ?? false);
      setPrefMarketing(stored?.marketing ?? false);
      setShowSettings(true);
      setShowBanner(true);
    };
    window.addEventListener(CONSENT_EVENT, open);
    return () => window.removeEventListener(CONSENT_EVENT, open);
  }, []);

  const apply = useCallback((statistics: boolean, marketing: boolean) => {
    const saved = writeConsent({ statistics, marketing });
    updateConsent(saved);
    setConsent(saved);
    setShowBanner(false);
    setShowSettings(false);
  }, []);

  // Seite sperren (kein Scroll/Klick), solange keine Cookie-Auswahl getroffen wurde.
  useEffect(() => {
    if (trackingConfigured && showBanner) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [showBanner]);

  const acceptAll = () => apply(true, true);
  const rejectAll = () => apply(false, false);
  const savePreferences = () => apply(prefStats, prefMarketing);

  return (
    <>
      {/* Scripts nur laden, wenn Consent vorliegt */}
      <Analytics consent={consent} />

      {/* Ohne konfiguriertes Tracking kein Banner nötig */}
      {trackingConfigured && showBanner && (
        <>
        {/* Blockierender Backdrop – abgedunkelt & geblurrt: klares Signal, dass eine Auswahl nötig ist */}
        <div className="fixed inset-0 z-[80] bg-header backdrop-blur-md" aria-hidden="true" />
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Cookie-Einwilligung"
        >
          <div className="w-full max-w-lg">
            <div className="card mx-auto w-full overflow-hidden border-line bg-page-alt p-5 shadow-2xl backdrop-blur-xl md:p-6">
              <div className="flex items-start gap-3">
                <span className="hidden h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-2 text-brand-ink sm:grid">
                  <Icon name="lock" size={20} />
                </span>
                <div className="flex-1">
                  <h2 className="font-display text-base font-semibold text-fg">
                    Wir respektieren Ihre Privatsphäre
                  </h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-fg-muted">
                    Wir verwenden Cookies, um unsere Website zu verbessern und – nur mit Ihrer
                    Zustimmung – anonyme Statistiken (Google Analytics via Tag Manager) zu erheben.
                    Notwendige Cookies sind für den Betrieb erforderlich. Mehr in der{" "}
                    <Link href="/cookie-richtlinie" className="text-brand-ink underline">
                      Cookie-Richtlinie
                    </Link>{" "}
                    und{" "}
                    <Link href="/datenschutz" className="text-brand-ink underline">
                      Datenschutzerklärung
                    </Link>
                    .
                  </p>

                  {/* Einstellungen (Kategorien) */}
                  {showSettings && (
                    <div className="mt-4 flex flex-col gap-2.5">
                      <CategoryRow
                        title="Notwendig"
                        desc="Für den Betrieb der Website und die Sicherheit erforderlich. Immer aktiv."
                        checked
                        disabled
                      />
                      <CategoryRow
                        title="Statistik"
                        desc="Anonyme Nutzungsanalyse (Google Analytics), um die Website zu verbessern."
                        checked={prefStats}
                        onChange={setPrefStats}
                      />
                      <CategoryRow
                        title="Marketing"
                        desc="Für Remarketing und Erfolgsmessung von Kampagnen (z. B. Google Ads)."
                        checked={prefMarketing}
                        onChange={setPrefMarketing}
                      />
                    </div>
                  )}

                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <button
                      type="button"
                      onClick={acceptAll}
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-brand-400"
                    >
                      Alle akzeptieren
                    </button>
                    {showSettings ? (
                      <button
                        type="button"
                        onClick={savePreferences}
                        className="inline-flex items-center justify-center rounded-md border border-line bg-surface-2 px-5 py-2.5 text-sm font-semibold text-fg transition-colors hover:border-line-strong"
                      >
                        Auswahl speichern
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowSettings(true)}
                        className="inline-flex items-center justify-center rounded-md border border-line bg-surface-2 px-5 py-2.5 text-sm font-semibold text-fg transition-colors hover:border-line-strong"
                      >
                        Einstellungen
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={rejectAll}
                      className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium text-fg-muted transition-colors hover:text-fg"
                    >
                      Nur notwendige
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
      )}
    </>
  );
}

function CategoryRow({
  title,
  desc,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label
      className={`flex items-start gap-3 rounded-xl border border-line bg-surface-2 p-3.5 ${
        disabled ? "opacity-70" : "cursor-pointer"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-line-strong bg-surface-2 accent-[color:var(--color-brand-500)]"
      />
      <span>
        <span className="block text-sm font-semibold text-fg">{title}</span>
        <span className="block text-xs leading-snug text-fg-subtle">{desc}</span>
      </span>
    </label>
  );
}
